import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../shared/Button'
import { CrmToaster, crmToast } from './crmUi'
import { useCrm } from './useCrm'
import { addTask, updateTask } from './crmApi'
import { apiGet, apiPost } from '../../utils/api'
import { OB, DropMenu, BtnPrimary, personColor, useObFont } from './obDesign'

/**
 * ONBOARDINGS MODULE · TO-DO PAGE — rebuilt to Natalie's Todo.dc.html mockup
 * (design rebuild 3/3, 30 Oct 2026). One flat table of everything
 * outstanding across onboardings: side tabs with count pills, mockup
 * dropdowns (Who · Type multi-tick · Company · Pathway · Group by),
 * grouped-by-company headers with the "N tasks for us · N overdue for X ·
 * N for them" sentence and the FOR-THE-CUSTOMER sub-label. Derived rows
 * (§3.2) keep their resolve-themselves behaviour.
 */

interface TodoRow {
  key: string; taskId: string | null; type: string; side: 'ours' | 'theirs'
  title: string; origin: string | null; company: string
  leadId: string | null; onboardingId: string | null
  ownerName: string | null; pathway: string | null; station: string | null
  dueAt: string | null; overdueDays: number | null
  action: 'open' | 'chase' | 'book' | 'reply' | 'review' | 'notify'
}

/** Type badge colours — KC map from the mockup. */
const KC: Record<string, [string, string]> = {
  'Reply needed': ['#E4EDFC', '#1D4ED8'], 'Need to chase': ['#FDEBC8', '#7A4600'],
  'Follow-up to send': ['#FFF4DE', '#7A4600'], 'Book a call': ['#FDE3E1', '#B42318'],
  'Check a file': ['#E3F4EA', '#146C3A'], 'Check in': ['#DDF3E4', '#146C3A'],
  'Plan alert': ['#FDE3E1', '#B42318'], 'Task': ['#F1F4F8', '#3A4555'],
}
const ALL_TYPES = ['Overdue', 'Unassigned', 'Task', 'Need to chase', 'Reply needed', 'Follow-up to send', 'Book a call', 'Check a file', 'Plan alert']
const GRID = '28px 2.8fr 1.3fr 1.2fr 1.1fr 1fr 1.2fr'
const ACTION_LABEL: Record<TodoRow['action'], string> = { open: 'Open', chase: 'Chase', book: 'Book', reply: 'Reply', review: 'Review', notify: 'Notify' }
const GO_LABEL: Record<string, string> = {
  'Task': 'Open station', 'Need to chase': 'Open chase email', 'Follow-up to send': 'Open draft',
  'Reply needed': 'Open thread', 'Book a call': 'Book next session', 'Check a file': 'Open files received',
  'Plan alert': 'Open plan',
}

export const CrmObTodo: React.FC = () => {
  useObFont()
  const navigate = useNavigate()
  const { data, reload } = useCrm<{ rows: TodoRow[]; pending: string[] }>(
    () => apiGet<{ rows: TodoRow[]; pending: string[] }>('/crm/onboarding/todos'), { rows: [], pending: [] })
  const [tab, setTab] = useState<'all' | 'ours' | 'theirs'>('all')
  const [gby, setGby] = useState<'None' | 'Company' | 'Person' | 'Type'>('Company')
  const [who, setWho] = useState('Everyone')
  const [types, setTypes] = useState<string[]>([])
  const [companyF, setCompanyF] = useState('All companies')
  const [pathF, setPathF] = useState('All pathways')
  const [q, setQ] = useState('')
  const [shut, setShut] = useState<Record<string, boolean>>({})
  const [addOpen, setAddOpen] = useState(false)

  const rows = data.rows
  const owners = useMemo(() => [...new Set(rows.map((r) => r.ownerName).filter(Boolean))].sort() as string[], [rows])
  const companies = useMemo(() => [...new Set(rows.map((r) => r.company))].sort(), [rows])
  const pathways = useMemo(() => [...new Set(rows.map((r) => r.pathway).filter(Boolean))].sort() as string[], [rows])

  const matchType = (r: TodoRow, k: string) => k === 'Overdue' ? (r.overdueDays || 0) > 0 : k === 'Unassigned' ? !r.ownerName && r.side === 'ours' : r.type === k
  const inSide = (r: TodoRow, s: typeof tab) => s === 'all' || r.side === s
  const base = useMemo(() => rows.filter((r) =>
    inSide(r, tab)
    && (who === 'Everyone' || (who === 'Unassigned' ? !r.ownerName : r.ownerName === who))
    && (companyF === 'All companies' || r.company === companyF)
    && (pathF === 'All pathways' || r.pathway === pathF)
    && (!q || `${r.title} ${r.company}`.toLowerCase().includes(q.toLowerCase()))
  ), [rows, tab, who, companyF, pathF, q])
  const filtered = useMemo(() => base.filter((r) => !types.length || types.some((k) => matchType(r, k)))
    .sort((a, b) => (b.overdueDays ?? -1) - (a.overdueDays ?? -1)), [base, types])

  const act = (r: TodoRow) => {
    if (r.action === 'chase' && r.taskId) {
      apiPost(`/crm/tasks/${r.taskId}/chase`, {}).then(() => { crmToast('Chased — clock restarted, draft raised on Follow-ups', 'success'); reload() }).catch(() => crmToast('Chase failed', 'error'))
      return
    }
    if (r.key.startsWith('draft:')) return navigate(`/crm/onboardings/followups?draft=${r.key.slice(6)}`)
    if (r.onboardingId) return navigate(`/crm/onboardings/co/${r.onboardingId}${r.action === 'book' ? '?tab=sessions' : r.action === 'reply' ? '?tab=comms' : r.action === 'notify' ? '?tab=plan' : ''}`)
    if (r.leadId) return navigate(`/crm/lead/${r.leadId}`)
  }

  const Row: React.FC<{ r: TodoRow }> = ({ r }) => {
    const od = (r.overdueDays || 0) > 0
    const c = KC[r.type] || KC.Task
    return (
      <div className="ob-row" style={{ display: 'grid', gridTemplateColumns: GRID, gap: 12, padding: '10px 16px', alignItems: 'center', borderBottom: `1px solid ${OB.rowDiv}`, fontSize: 13 }}>
        {r.taskId ? (
          <button aria-label="Complete" onClick={() => updateTask(r.taskId!, { done: true }).then(() => { crmToast('Done ✓', 'success'); reload() })}
            style={{ width: 18, height: 18, borderRadius: 4, cursor: 'pointer', padding: 0, background: '#fff', border: `1.5px solid ${OB.faint2}` }} />
        ) : <span title="Derived — resolves itself when the underlying thing is handled" style={{ width: 18, height: 18, borderRadius: '50%', border: `1.5px dashed ${OB.inputBorder}`, boxSizing: 'border-box' }} />}
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <button onClick={() => act(r)} style={{ font: 'inherit', fontWeight: 700, color: OB.ink, background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</button>
          <span style={{ fontSize: 12, color: OB.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.origin || ''}{r.origin ? ' · ' : ''}
            <button onClick={() => act(r)} style={{ font: 'inherit', color: OB.accent, fontWeight: 700, background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>{GO_LABEL[r.type] || 'Open'} →</button>
          </span>
        </span>
        <button onClick={() => r.onboardingId ? navigate(`/crm/onboardings/co/${r.onboardingId}`) : r.leadId && navigate(`/crm/lead/${r.leadId}`)}
          style={{ font: 'inherit', fontWeight: 700, color: OB.ink, background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.company}</button>
        <span style={{ fontSize: 11.5, fontWeight: 800, borderRadius: 6, padding: '2px 8px', justifySelf: 'start', whiteSpace: 'nowrap', background: c[0], color: c[1] }}>{r.type}</span>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
          <span style={{ width: 24, height: 24, flex: '0 0 24px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#fff', background: r.ownerName ? personColor(r.ownerName, r.side === 'theirs') : OB.red }}>{r.ownerName ? r.ownerName.slice(0, 2).toUpperCase() : '?'}</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...(r.ownerName ? { color: OB.ink } : { color: OB.red, fontWeight: 800 }) }}>{r.ownerName ? `${r.ownerName}${r.side === 'theirs' ? ' (them)' : ''}` : 'Unassigned'}</span>
        </span>
        <span style={{ fontSize: 12.5, whiteSpace: 'nowrap', ...(od ? { color: OB.red, fontWeight: 800 } : { color: OB.ink2 }) }}>
          {od ? `Overdue ${r.overdueDays}d` : r.dueAt ? `Due ${new Date(r.dueAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
        </span>
        <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button onClick={() => act(r)} style={{ font: 'inherit', fontSize: 12, fontWeight: 700, background: OB.track, color: OB.ink, border: 0, borderRadius: 6, padding: '4px 9px', cursor: 'pointer' }}>{ACTION_LABEL[r.action]}</button>
        </span>
      </div>
    )
  }

  const keyOf = (r: TodoRow) => gby === 'Company' ? r.company : gby === 'Person' ? (r.ownerName || 'Unassigned') : r.type
  const groups = useMemo(() => {
    if (gby === 'None') return [{ name: '', rows: filtered.filter((r) => r.side === 'ours' || tab !== 'all'), trows: tab === 'all' ? filtered.filter((r) => r.side === 'theirs') : [] }]
    const names = [...new Set(filtered.map(keyOf))].sort((a, b) => {
      const au = filtered.some((r) => keyOf(r) === a && r.side === 'ours') ? 0 : 1
      const bu = filtered.some((r) => keyOf(r) === b && r.side === 'ours') ? 0 : 1
      return au - bu
    })
    return names.map((n) => {
      const list = filtered.filter((r) => keyOf(r) === n)
      return { name: n, rows: list.filter((r) => r.side === 'ours'), trows: list.filter((r) => r.side === 'theirs') }
    })
  }, [filtered, gby, tab])

  const countOf = (t: typeof tab) => rows.filter((r) => inSide(r, t)).length
  const overdueN = rows.filter((r) => (r.overdueDays || 0) > 0).length

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: OB.pageBg, fontFamily: OB.font, color: OB.ink }}>
      <CrmToaster />
      <div style={{ flex: 'none', padding: '16px 28px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <header style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>To-do</h1>
              <span style={{ fontSize: 12.5, color: OB.muted }}>{rows.length} open across {companies.length} compan{companies.length === 1 ? 'y' : 'ies'} · {overdueN} overdue</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 10, padding: '0 12px', minHeight: 40, width: 260, boxSizing: 'border-box' }}>
                <span style={{ fontSize: 12.5, color: OB.muted }}>Search</span>
                <input aria-label="Search tasks" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Task or company" style={{ border: 0, outline: 0, font: 'inherit', flex: 1, minWidth: 0 }} />
              </label>
              <BtnPrimary small onClick={() => setAddOpen(true)}>+ Add task</BtnPrimary>
            </div>
          </header>

          {/* side tabs + view toggle */}
          <div role="tablist" style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${OB.border}`, flexWrap: 'wrap' }}>
            {([['all', 'All tasks'], ['ours', 'Ours'], ['theirs', 'Theirs']] as const).map(([k, l]) => {
              const on = tab === k
              return (
                <button key={k} role="tab" onClick={() => setTab(k)} style={{ font: 'inherit', fontSize: 14, background: 'none', border: 0, padding: '10px 14px', minHeight: 44, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, ...(on ? { fontWeight: 800, color: OB.accent, borderBottom: `3px solid ${OB.accent}` } : { color: OB.muted, borderBottom: '3px solid transparent' }) }}>
                  {l} <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 999, padding: '0 6px', background: on ? OB.accentSoft : OB.track, color: on ? OB.accent : OB.muted }}>{countOf(k)}</span>
                </button>
              )
            })}
            <div style={{ marginLeft: 'auto', alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12.5, color: OB.muted }}>View</span>
              <div style={{ display: 'flex', border: `1px solid ${OB.border}`, borderRadius: 8, overflow: 'hidden' }}>
                {([['List', 'None'], ['By company', 'Company']] as const).map(([l, g]) => {
                  const on = g === 'Company' ? gby === 'Company' : gby !== 'Company'
                  return <button key={l} onClick={() => setGby(g as any)} aria-pressed={on} style={{ font: 'inherit', fontSize: 13, border: 0, padding: '0 12px', minHeight: 34, cursor: 'pointer', background: on ? OB.accent : '#fff', color: on ? '#fff' : OB.ink, fontWeight: on ? 700 : 400 }}>{l}</button>
                })}
              </div>
            </div>
          </div>

          {/* filter dropdowns */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <DropMenu name="Who" value={who} active={who !== 'Everyone'}
              options={['Everyone', ...owners, 'Unassigned'].map((l) => ({ label: l, n: rows.filter((r) => inSide(r, tab) && (l === 'Everyone' || (l === 'Unassigned' ? !r.ownerName : r.ownerName === l))).length, on: who === l, pick: () => setWho(l) }))} />
            <DropMenu name="Type" value={types.length === 0 ? 'All types' : types.length === 1 ? types[0] : `${types.length} selected`} active={types.length > 0} multi onClear={() => setTypes([])}
              options={ALL_TYPES.map((k) => ({ label: k, n: base.filter((r) => matchType(r, k)).length, on: types.includes(k), pick: () => setTypes(types.includes(k) ? types.filter((x) => x !== k) : [...types, k]) }))} />
            <DropMenu name="Company" value={companyF} active={companyF !== 'All companies'}
              options={['All companies', ...companies].map((l) => ({ label: l, n: rows.filter((r) => inSide(r, tab) && (l === 'All companies' || r.company === l)).length, on: companyF === l, pick: () => setCompanyF(l) }))} />
            <DropMenu name="Pathway" value={pathF} active={pathF !== 'All pathways'}
              options={['All pathways', ...pathways].map((l) => ({ label: l, on: pathF === l, pick: () => setPathF(l) }))} />
            <DropMenu name="Group by" value={gby} active={gby !== 'None'}
              options={(['None', 'Company', 'Person', 'Type'] as const).map((l) => ({ label: l, on: gby === l, pick: () => setGby(l) }))} />
            <span style={{ fontSize: 12.5, color: OB.muted, alignSelf: 'center', marginLeft: 'auto' }}>{filtered.length} tasks · most overdue first</span>
          </div>

      </div>

      {/* table — scrolls under the fixed chrome */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 28px 16px' }}>
          <section style={{ background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 12, overflow: 'auto', flex: 1, minHeight: 0 }}>
            <div style={{ minWidth: 1000 }}>
              <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 12, padding: '10px 16px', fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', color: OB.faint, borderBottom: `1px solid ${OB.border}` }}>
                <span /><span>TASK</span><span>COMPANY</span><span>TYPE</span><span>WHO</span><span>DUE</span><span />
              </div>
              {groups.map((g) => {
                const key = `${gby}:${g.name}`
                const isShut = gby !== 'None' && !!shut[key]
                const over = g.rows.filter((r) => (r.overdueDays || 0) > 0)
                const overWho = [...new Set(over.map((r) => (r.ownerName || 'Unassigned').split(' ')[0]))]
                const thOd = g.trows.filter((r) => (r.overdueDays || 0) > 0).length
                const sentence = (g.rows.length ? `${g.rows.length} task${g.rows.length === 1 ? '' : 's'} for us` : 'Nothing for us')
                  + (over.length ? ` · ${over.length} overdue for ${overWho.join(', ')}` : '')
                  + (g.trows.length ? ` · ${g.trows.length} for ${gby === 'Company' ? g.name : 'the customer'} to do${thOd ? ` (${thOd} overdue)` : ''}` : '')
                return (
                  <React.Fragment key={key}>
                    {gby !== 'None' && (
                      <button onClick={() => setShut({ ...shut, [key]: !isShut })} aria-expanded={!isShut}
                        style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8, textAlign: 'left', font: 'inherit', cursor: 'pointer', padding: '10px 16px', fontSize: 13, fontWeight: 800, color: OB.ink2, background: OB.headerBg, border: 0, borderBottom: `1px solid ${OB.rowDiv}`, boxSizing: 'border-box' }}>
                        <span aria-hidden style={{ width: 12, color: OB.muted }}>{isShut ? '▸' : '▾'}</span>
                        {g.name} <span style={{ color: OB.faint, fontWeight: 700 }}>· {sentence}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: OB.faint }}>{isShut ? 'Show' : 'Hide'}</span>
                      </button>
                    )}
                    {!isShut && g.rows.map((r) => <Row key={r.key} r={r} />)}
                    {!isShut && g.trows.length > 0 && (
                      <div style={{ padding: '8px 16px 6px 36px', fontSize: 12, fontWeight: 800, color: OB.muted, background: '#FBFCFE', borderBottom: `1px solid ${OB.rowDiv}`, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                        For {gby === 'Company' && g.name ? g.name : 'the customer'} to do · {g.trows.length}
                      </div>
                    )}
                    {!isShut && g.trows.map((r) => <Row key={r.key} r={r} />)}
                  </React.Fragment>
                )
              })}
              {!filtered.length && <div style={{ padding: '24px 16px', fontSize: 13.5, color: OB.muted }}>Nothing here. Clear a filter to see more.</div>}
            </div>
          </section>
          {data.pending.length > 0 && <span style={{ fontSize: 12, color: OB.muted }}>Coming with later phases: {data.pending.join(' · ')}</span>}
      </div>

      {addOpen && <AddTaskModal companies={rows} onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); reload() }} />}
    </div>
  )
}

/* + Add task (§2.14): text, owner, side, due (defaults by side), company, pathway/station. */
const AddTaskModal: React.FC<{ companies: TodoRow[]; onClose: () => void; onAdded: () => void }> = ({ companies, onClose, onAdded }) => {
  const opts = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of companies) if (r.leadId) m.set(r.leadId, r.company)
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [companies])
  const [leadId, setLeadId] = useState(opts[0]?.[0] || '')
  const [title, setTitle] = useState('')
  const [side, setSide] = useState<'ours' | 'theirs'>('ours')
  const [ownerName, setOwnerName] = useState('')
  const [due, setDue] = useState<string>('')
  const [pathway, setPathway] = useState('')
  const [station, setStation] = useState('')
  const inputCls = 'rounded-lg border border-border bg-surface px-2.5 py-1.5 text-[12px] w-full focus:outline-none focus:border-brand-400'
  const save = () => {
    if (!title.trim() || !leadId) { crmToast('Pick a company and write the task', 'error'); return }
    addTask(leadId, {
      title: title.trim(), side, ownerName: ownerName.trim() || null,
      dueInDays: due ? Math.max(0, Math.round((new Date(due).getTime() - Date.now()) / 864e5)) : undefined,
      pathway: pathway.trim() || null, station: station.trim() || null,
    } as any).then(() => { crmToast('Task added', 'success'); onAdded() }).catch(() => crmToast('Not saved', 'error'))
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="rounded-xl bg-surface border border-border p-4 w-full max-w-[440px] space-y-2.5" onClick={(e) => e.stopPropagation()}>
        <p className="text-[14px] font-bold text-txt-primary">Add task</p>
        <select value={leadId} onChange={(e) => setLeadId(e.target.value)} className={inputCls}>
          {opts.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs doing?" className={inputCls} autoFocus />
        <div className="flex gap-2">
          <select value={side} onChange={(e) => setSide(e.target.value as any)} className={inputCls}>
            <option value="ours">Ours</option>
            <option value="theirs">Theirs (the customer's)</option>
          </select>
          <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder={side === 'ours' ? 'Owner (person)' : 'Owner (their person)'} className={inputCls} />
        </div>
        <div className="flex gap-2">
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={inputCls} title={`Default: ${side === 'theirs' ? '7' : '4'} days`} />
          <input value={pathway} onChange={(e) => setPathway(e.target.value)} placeholder="Pathway (optional)" className={inputCls} />
          <input value={station} onChange={(e) => setStation(e.target.value)} placeholder="Station (optional)" className={inputCls} />
        </div>
        <p className="text-[10.5px] text-txt-tertiary">No due date → default: ours 4 days, theirs 7.</p>
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={save}>Add task</Button>
        </div>
      </div>
    </div>
  )
}

export default CrmObTodo
